import React, { useState, useEffect, useRef } from 'react';
import { 
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import useScrollLock from '../../hooks/useScrollLock';

const ExternalLinkWarningModal = ({ 
  isOpen, 
  targetUrl, 
  onContinue, 
  onCancel 
}) => {
  const [rememberChoice, setRememberChoice] = useState(false);
  
  // Use scroll lock hook to prevent background scrolling
  const { isLocked } = useScrollLock({ 
    isActive: isOpen,
    restoreScrollOnUnmount: true 
  });

  // Reset checkbox state when modal opens
  useEffect(() => {
    if (isOpen) {
      setRememberChoice(false);
    }
  }, [isOpen]);
  const modalRef = useRef(null);
  const continueButtonRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const checkboxRef = useRef(null);

  // Focus management
  useEffect(() => {
    if (isOpen) {
      // Focus the continue button when modal opens
      setTimeout(() => {
        if (continueButtonRef.current) {
          continueButtonRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          handleCancel();
          break;
        case 'Tab':
          handleTabNavigation(event);
          break;
        case 'Enter':
          // If focus is on continue button or checkbox, continue
          if (document.activeElement === continueButtonRef.current ||
              document.activeElement === checkboxRef.current) {
            event.preventDefault();
            handleContinue();
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, rememberChoice]);

  // Focus trap implementation
  const handleTabNavigation = (event) => {
    const focusableElements = modalRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (!focusableElements || focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      // Shift + Tab (backward)
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab (forward)
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  };

  const handleContinue = () => {
    if (onContinue) {
      onContinue(rememberChoice);
    }
    setRememberChoice(false); // Reset for next time
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    setRememberChoice(false); // Reset for next time
  };

  const handleCheckboxChange = (event) => {
    setRememberChoice(event.target.checked);
  };

  // Get domain from URL for display
  const getDisplayDomain = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch (error) {
      return url;
    }
  };

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  return (
    <div 
      className="modal-backdrop"
      aria-labelledby="external-link-modal-title"
      aria-describedby="external-link-modal-description"
      role="dialog"
      aria-modal="true"
      onClick={handleCancel}
    >
      {/* Modal container */}
      <div 
        ref={modalRef}
        className="modal-container max-w-lg"
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        {/* Header */}
        <div className="modal-header">
          <div className="flex items-center justify-between">
            <h3 
              className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
              id="external-link-modal-title"
            >
              You're leaving this site
            </h3>
            
            <button
              type="button"
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 
                       hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              onClick={handleCancel}
              aria-label="Close modal"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="modal-scroll-container">
          <div className="modal-content" id="external-link-modal-description">
            <div className="sm:flex sm:items-start">
            {/* Warning icon */}
            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/30 sm:mx-0 sm:h-10 sm:w-10">
              <ExclamationTriangleIcon 
                className="h-6 w-6 text-yellow-600 dark:text-yellow-400" 
                aria-hidden="true" 
              />
            </div>

            {/* Modal text content */}
            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
              <div className="mt-2">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  You're about to visit an external website. This link will open in a new tab.
                </p>
                
                {/* URL display */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-md p-3 mb-4">
                  <div className="flex items-center space-x-2">
                    <ArrowTopRightOnSquareIcon 
                      className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" 
                      aria-hidden="true" 
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {getDisplayDomain(targetUrl)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {targetUrl}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Remember choice checkbox */}
                <div className="flex items-start space-x-2">
                  <input
                    ref={checkboxRef}
                    id="remember-choice"
                    type="checkbox"
                    checked={rememberChoice}
                    onChange={handleCheckboxChange}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500 dark:bg-gray-700 dark:focus:ring-purple-400"
                    aria-describedby="remember-choice-description"
                  />
                  <div className="flex-1">
                    <label 
                      htmlFor="remember-choice" 
                      className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                    >
                      Don't show this warning again
                    </label>
                    <p 
                      id="remember-choice-description"
                      className="text-xs text-gray-500 dark:text-gray-400 mt-1"
                    >
                      External links will open directly in new tabs without this confirmation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
            <button
              ref={cancelButtonRef}
              type="button"
              className="mt-3 sm:mt-0 inline-flex w-full sm:w-auto justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-200 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              ref={continueButtonRef}
              type="button"
              className="inline-flex w-full sm:w-auto justify-center rounded-md bg-purple-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
              onClick={handleContinue}
            >
              Continue to site
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExternalLinkWarningModal;